import React, { useState, useEffect, useRef } from "react";
import {
  Type,
  Video,
  Music,
  FileText,
  Layers,
  Sparkles,
  Star,
  Mic,
  BookOpen,
  Volume2,
  Trash2,
  PenTool,
  Ruler, // Icon for Guide/Tracking mode
  Eraser, // Icon for Practice mode
  Timer, // New icon for 'Coming Soon'
} from "lucide-react";

// ======================= SPEECH UTILITY FUNCTION =======================
const speakText = (text, selectedVoice) => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.8;
    utterance.pitch = 1.2;

    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    window.speechSynthesis.speak(utterance);
  } else {
    console.warn("Speech Synthesis not supported in this browser.");
  }
};


// ======================= TRACING BOARD COMPONENT (Unchanged) =======================
const TracingBoard = ({ letter, setLetter, keyWord }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [penColor, setPenColor] = useState('#6D28D9');
  const [penSize, setPenSize] = useState(10);
  const [isReady, setIsReady] = useState(false);
  const [tracingMode, setTracingMode] = useState('guide');

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  // Redraw canvas when letter or mode changes or window resizes
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();

        // Important for responsiveness: reset internal dimensions
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);

        drawLetterGuide(ctx, rect.width, rect.height, tracingMode);
        setIsReady(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [letter, tracingMode]);

  const drawLetterGuide = (ctx, width, height, mode) => {
    ctx.clearRect(0, 0, width, height);
    if (mode === "practice") return;

    const padding = 20;
    const drawingHeight = height - padding * 2;
    const topY = padding + drawingHeight * 0.1;
    const midY = padding + drawingHeight * 0.5;
    const bottomY = padding + drawingHeight * 0.9;

    ctx.strokeStyle = "rgba(128,128,128,0.3)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(padding, bottomY); ctx.lineTo(width - padding, bottomY); ctx.stroke();
    ctx.setLineDash([5, 5]);
    ctx.beginPath(); ctx.moveTo(padding, midY); ctx.lineTo(width - padding, midY); ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(padding, topY); ctx.lineTo(width - padding, topY); ctx.stroke();

    const letterFontSize = drawingHeight * 0.7;
    const keywordFontSize = Math.max(12, drawingHeight * 0.1); // Responsive font floor

    ctx.font = `bold ${letterFontSize}px Arial`;
    ctx.fillStyle = "rgba(109,40,217,0.25)";
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";

    const x = width / 2;
    const textY = bottomY - (drawingHeight * 0.15);
    
    // Adjust spacing for mobile
    const offsetX = width < 400 ? width / 3.5 : width / 4;

    ctx.fillText(letter, x - offsetX, textY);
    ctx.fillText(letter.toLowerCase(), x + offsetX, textY);

    ctx.font = `${keywordFontSize}px Arial`;
    ctx.fillStyle = "rgba(255,99,71,0.45)";
    ctx.textBaseline = "top";
    ctx.fillText(`— ${keyWord} —`, x, textY + keywordFontSize * 0.2);
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Support both mouse and touch
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    // Stop event from bubbling/scrolling
    if (e.cancelable) e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || !isReady) return;
    // VERY IMPORTANT: Stops the page from scrolling while drawing
    if (e.cancelable) e.preventDefault();

    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");

    ctx.lineWidth = penSize;
    ctx.strokeStyle = penColor;
    ctx.lineCap = "round"; // Makes drawing smoother
    ctx.lineJoin = "round";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    drawLetterGuide(ctx, rect.width, rect.height, tracingMode);
  };

  return (
    <div className="border-2 border-green-300 rounded-2xl shadow-xl bg-white overflow-hidden flex flex-col w-full max-w-4xl mx-auto">
      
      {/* 🔤 Responsive Alphabet Selection Bar */}
      <div className="flex overflow-x-auto gap-2 p-3 bg-purple-50 border-b scrollbar-hide">
        {alphabet.map((char) => (
          <button
            key={char}
            onClick={() => setLetter(char)}
            className={`flex-shrink-0 w-10 h-10 rounded-xl text-lg font-bold transition-all
              ${char === letter
                ? "bg-purple-600 text-white shadow-lg scale-105"
                : "bg-white border-2 text-gray-700 hover:border-purple-300"
              }`}
          >
            {char}
          </button>
        ))}
      </div>

      {/* Responsive Controls */}
      <div className="grid grid-cols-2 sm:flex items-center justify-between p-4 bg-green-50 gap-4">
        <button
          onClick={() => setTracingMode(tracingMode === "guide" ? "practice" : "guide")}
          className={`flex items-center justify-center px-4 py-2 rounded-xl shadow-sm border-2 font-bold text-sm transition-all
            ${tracingMode === "guide" ? "bg-green-600 text-white border-green-700" : "bg-white text-gray-700 border-gray-200"}`}
        >
          {tracingMode === "guide" ? "📏 Guide" : "✏️ Practice"}
        </button>

        <div className="flex items-center justify-center space-x-2">
          <input
            type="color"
            value={penColor}
            onChange={(e) => setPenColor(e.target.value)}
            className="w-10 h-10 rounded-lg cursor-pointer border-2 border-white shadow-sm"
          />
          <button
            onClick={handleClear}
            className="p-2 bg-white border-2 border-red-100 text-red-500 rounded-xl hover:bg-red-50 transition-colors"
          >
            <Trash2 size={20} />
          </button>
        </div>

        <div className="col-span-2 flex items-center space-x-3 bg-white/50 p-2 rounded-lg border border-green-100">
          <span className="text-xs font-bold text-green-800">PEN SIZE</span>
          <input
            type="range"
            min="4"
            max="30"
            value={penSize}
            onChange={(e) => setPenSize(e.target.value)}
            className="flex-grow accent-green-600 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>
      </div>

      {/* Canvas Wrapper */}
      <div className="bg-white relative touch-none select-none overflow-hidden h-[300px] sm:h-[450px]">
        <canvas
          ref={canvasRef}
          style={{ 
            touchAction: 'none', // STOPS BROWSER SCROLLING
            width: '100%', 
            height: '100%',
            display: 'block' 
          }}
          className="cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />

        {!isReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        )}
      </div>
    </div>
  );
};

// =========================================================================


// ======================= ACTIVITY DISPLAY COMPONENT (Unchanged) =======================
const ActivityDisplay = ({ activity, letter, words, keyWord }) => {
  const [currentLetter, setCurrentLetter] = useState("A");

  const KEYWORDS = {
    A: "Apple",
    B: "Ball",
    C: "Cat",
    D: "Dog",
    E: "Elephant",
    F: "Fish",
    G: "Goat",
    H: "Hat",
    I: "Ice Cream",
    J: "Jug",
    K: "Kite",
    L: "Lion",
    M: "Monkey",
    N: "Nest",
    O: "Orange",
    P: "Parrot",
    Q: "Queen",
    R: "Rabbit",
    S: "Sun",
    T: "Tiger",
    U: "Umbrella",
    V: "Van",
    W: "Watch",
    X: "Xylophone",
    Y: "Yak",
    Z: "Zebra"
  };
  // Added keyWord prop
  if (!activity) {
    return (
      <div className="text-center p-10 bg-purple-50 rounded-2xl shadow-inner text-gray-600">
        <Star className="w-10 h-10 text-pink-500 mx-auto mb-3" />
        <p className="text-lg font-semibold">
          Select an activity from the sidebar menu to start your interactive learning for {letter}!
        </p>
      </div>
    );
  }

  const renderContent = () => {
    switch (activity.key) {
      case "video":
        return (
          <div className="space-y-4">
            <h4 className="text-xl font-bold text-red-600">
              Video: How to Write '{letter}'
            </h4>
            <div className="aspect-video bg-gray-300 rounded-lg flex items-center justify-center">
              <span className="text-gray-600">
                [Mock Video Player for Letter {letter}]
              </span>
            </div>
            <p className="text-gray-700">
              Watch this fun animation to see how to form the upper and lower case letters.
            </p>
          </div>
        );
      case "song":
        return (
          <div className="space-y-4">
            <h4 className="text-xl font-bold text-blue-600">
              Phonics Song: The Sound of '{letter}'
            </h4>
            <div className="h-24 bg-blue-100 rounded-lg flex items-center justify-center shadow-md">
              <Music className="w-6 h-6 text-blue-600 mr-2" />
              <span className="text-blue-800">
                [Mock Audio Player with Lyrics for {letter} Song]
              </span>
            </div>
            <p className="text-gray-700">
              Sing along and practice making the /{letter.toLowerCase().padEnd(2)}/ sound!
            </p>
          </div>
        );
      case "quiz":
        return (
          <div className="space-y-4">
            <h4 className="text-xl font-bold text-purple-600">
              Interactive Quiz
            </h4>
            <div className="bg-purple-100 p-4 rounded-lg shadow-md">
              <p className="font-semibold mb-2">
                Which word starts with the sound of '{letter}'?
              </p>
              <div className="flex flex-wrap gap-3">
                {/* Mock Quiz Options */}
                {words.map((w, index) => (
                  <button
                    key={index}
                    className="bg-white text-purple-700 px-4 py-2 rounded-xl shadow hover:bg-purple-200 transition"
                  >
                    {w}
                  </button>
                ))}
              </div>
              <button className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-xl hover:bg-purple-700">
                Check Answer
              </button>
            </div>
          </div>
        );
      case "tracing":
        return (
          <div className="space-y-4">
            <h4 className="text-xl font-bold text-green-600">Digital Tracing Board</h4>

            {/* Pass keyWord to TracingBoard */}
            <TracingBoard
              letter={currentLetter}
              setLetter={setCurrentLetter}
              keyWord={KEYWORDS[currentLetter]}
            />

            <p className="text-gray-700">
              Use your mouse or finger to trace the letter directly on the screen!
            </p>
          </div>
        );
      default:
        return (
          <div className="text-center p-10 bg-gray-50 rounded-2xl shadow-inner text-gray-500">
            <Timer className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
            <p className="text-lg font-semibold">
              {activity.title} is coming soon for letter {letter}!
            </p>
          </div>
        );
    }
  };

  return (
    <div className="bg-white p-6 rounded-3xl shadow-2xl border border-gray-100 space-y-4">
      <h3 className="text-3xl font-extrabold text-purple-700 border-b pb-3 mb-4">
        {activity.title}
      </h3>
      {renderContent()}
    </div>
  );
};
// =========================================================================


// ======================= ACTIVITY CARD COMPONENT (Updated) =======================
const ActivityCard = ({ title, icon, color, activityKey, onClick, isActive, status }) => {
  const isComingSoon = status === 'coming_soon';

  const handleClick = (e) => {
    if (isComingSoon) {
      console.log(`${title} is coming soon!`);
      // Optionally prevent propagation if needed, but not necessary here since the main handler is checked below
    } else {
      onClick(activityKey, title);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`p-4 rounded-xl shadow-md flex items-center gap-3 transition cursor-pointer border-l-4
        ${isComingSoon
          ? 'bg-gray-200 border-gray-400 text-gray-500 opacity-60 grayscale cursor-not-allowed'
          : isActive
            ? 'bg-pink-100 border-pink-500 ring-2 ring-pink-300 font-bold text-gray-800'
            : 'bg-white border-purple-100 hover:bg-purple-50 text-gray-800'
        }
      `}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${color}`}>{icon}</div>
      <div className="flex-1">
        <h4 className={`text-base font-semibold ${isComingSoon ? 'line-through-none' : ''}`}>
          {title}
          {isComingSoon && (
            <span className="ml-2 inline-flex items-center text-xs font-bold text-yellow-700 bg-yellow-300/50 px-2 py-0.5 rounded-full ring-1 ring-yellow-400">
              <Timer className="w-3 h-3 mr-1" /> Soon
            </span>
          )}
        </h4>
      </div>
    </div>
  );
};


// ======================= MAIN PAGE COMPONENT =======================
const AlphabetPage = () => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const mockWords = [
    ["Apple", "Ant", "Axe"], ["Ball", "Bear", "Boat"], ["Cat", "Car", "Cone"],
    ["Dog", "Duck", "Door"], ["Egg", "Elephant", "Ear"], ["Fish", "Fan", "Frog"],
    ["Grape", "Goat", "Game"], ["Hat", "House", "Horse"], ["Ice", "Igloo", "Ink"],
    ["Jam", "Jet", "Jelly"], ["Kite", "Key", "King"], ["Lion", "Lamp", "Leaf"],
    ["Mouse", "Moon", "Mug"], ["Nose", "Net", "Nest"], ["Orange", "Owl", "Octopus"],
    ["Pig", "Pencil", "Pen"], ["Queen", "Quilt", "Quiet"], ["Rabbit", "Ring", "Rope"],
    ["Sun", "Star", "Sock"], ["Tiger", "Tree", "Toy"], ["Umbrella", "Up", "Under"],
    ["Van", "Vase", "Vest"], ["Watch", "Water", "Worm"], ["X-ray", "Xylophone"],
    ["Yarn", "Yo-yo", "Yacht"], ["Zebra", "Zip", "Zoo"]
  ];

  const [selectedLetter, setSelectedLetter] = useState("A");
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [story, setStory] = useState("");

  const [voices, setVoices] = useState([]);
  const [preferredVoice, setPreferredVoice] = useState(null);
  const voiceListRef = useRef([]);

  const letterIndex = selectedLetter.charCodeAt(0) - 65;
  const words = mockWords[letterIndex] || [];
  const keyWord = words[0] || "Unknown"; // Primary keyword

  // UPDATED ACTIVITIES: Last three are 'coming_soon'
  const activities = [
    { key: "video", title: "Alphabet Video", icon: <Video className="text-white w-5 h-5" />, color: "bg-red-500", status: 'live' },
    { key: "song", title: "Phonics Song", icon: <Music className="text-white w-5 h-5" />, color: "bg-blue-500", status: 'live' },
    { key: "tracing", title: "Tracing Board", icon: <FileText className="text-white w-5 h-5" />, color: "bg-green-500", status: 'live' },
    { key: "flashcards", title: "Vocabulary Flashcards", icon: <Layers className="text-white w-5 h-5" />, color: "bg-orange-500", status: 'coming_soon' },
    { key: "quiz", title: "Interactive Quiz", icon: <Star className="text-white w-5 h-5" />, color: "bg-purple-500", status: 'coming_soon' },
    { key: "spell", title: "Read & Spell Game", icon: <Type className="text-white w-5 h-5" />, color: "bg-pink-500", status: 'coming_soon' },
  ];

  // Simple static story generator
  const createStory = (letter, wordList) => {
    const w = wordList.length >= 3 ? wordList : ["Word 1", "Word 2", "Word 3"];
    return `
      One sunny afternoon, ${w[0]}, ${w[1]}, and ${w[2]} decided to have a picnic. 
      They packed their bags and set off on a thrilling adventure led by the cheerful letter 
      <strong>${letter}</strong>! They learned that ${letter} is the start of many amazing things.
    `;
  };

  // ASYNCHRONOUS VOICE LOADING
  useEffect(() => {
    if ('speechSynthesis' in window) {
      const loadVoices = () => {
        const currentVoices = window.speechSynthesis.getVoices();
        if (currentVoices.length > 0 && voiceListRef.current.length === 0) {
          voiceListRef.current = currentVoices;
          setVoices(currentVoices);

          const preferred = currentVoices.find(v =>
            v.lang.startsWith('en-') &&
            (v.name.includes('Kid') || v.name.includes('Zarvox') || v.name.includes('US English'))
          ) || currentVoices.find(v => v.lang.startsWith('en-'));

          setPreferredVoice(preferred);
        }
      };

      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = loadVoices;
      }
      loadVoices();
    }
  }, []);

  const handleLetterClick = (letter) => {
    setSelectedLetter(letter);
    speakText(letter, preferredVoice);
  };

  const handleKeywordSpeak = () => {
    const speechText = `${selectedLetter} for ${keyWord}`;
    speakText(speechText, preferredVoice);
  };

  useEffect(() => {
    setStory(createStory(selectedLetter, words));
    // Reset selected activity if it's 'coming_soon' or just generally on letter change
    if (selectedActivity?.status === 'coming_soon' || selectedActivity === null) {
      setSelectedActivity(null);
    }
  }, [selectedLetter]);

  const handleActivityClick = (key, title) => {
    const activity = activities.find(a => a.key === key);
    if (activity.status !== 'coming_soon') {
      setSelectedActivity(activity);
    } else {
      // This case is handled by the ActivityCard, but here for redundancy:
      setSelectedActivity(activity); // Set the activity to show the "Coming Soon" message in the main content area
      console.log(`${title} is coming soon! Cannot select for interaction.`);
    }
  };

  const handleVoiceChange = (event) => {
    const voiceName = event.target.value;
    const newVoice = voices.find(v => v.name === voiceName);
    setPreferredVoice(newVoice);
    speakText(selectedLetter, newVoice);
  };


  return (
    <div className="min-h-screen bg-[#F8F5FF] p-5 font-sans">

      {/* ======================= HEADER ======================= */}
      <div className="w-full mb-8 bg-white p-6 rounded-3xl shadow-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
          {/* Title Block */}
          <div className="flex items-center gap-4 mb-2 sm:mb-0">
            <Sparkles className="w-8 h-8 text-pink-500" />
            <h1 className="text-4xl md:text-5xl font-extrabold text-purple-700 tracking-tight">
              Alphabet Learning Hub
            </h1>
          </div>

          {/* VOICE SELECTION DROPDOWN */}
          {'speechSynthesis' in window && (
            <div className="flex items-center space-x-2 bg-purple-100 p-2 rounded-xl mt-3 sm:mt-0 max-w-full">
              <Mic className="w-5 h-5 text-purple-600 flex-shrink-0" />
              <label htmlFor="voice-select" className="text-sm font-semibold text-purple-700 flex-shrink-0">Voice:</label>
              <select
                id="voice-select"
                onChange={handleVoiceChange}
                value={preferredVoice ? preferredVoice.name : ''}
                className="p-1 rounded bg-white border border-purple-300 text-sm text-gray-700 w-full sm:w-auto min-w-40"
                disabled={voices.length === 0}
              >
                {voices.length === 0 ? (
                  <option>Loading voices...</option>
                ) : (
                  voices.map((voice) => (
                    <option key={voice.name} value={voice.name}>
                      {voice.name} ({voice.lang})
                    </option>
                  ))
                )}
              </select>
            </div>
          )}
        </div>
        <p className="text-gray-600 text-lg mt-2">
          Explore pronunciation, vocabulary, engaging videos, and fun songs for every letter.
        </p>
      </div>

      {/* ======================= MAIN CONTENT GRID ======================= */}
      <div className="w-full grid lg:grid-cols-4 gap-8">

        {/* ======================= LEFT COLUMN (SIDEBAR) ======================= */}
        <div className="lg:col-span-1 space-y-6">

          {/* LETTER PICKER CONTAINER */}
          <div>
            <h2 className="text-xl font-bold mb-3 text-purple-700 border-l-4 border-purple-400 pl-3">
              Choose a Letter:
            </h2>
            <div className="bg-white rounded-3xl p-4 shadow-xl border border-purple-200 space-y-4">
              {/* Alphabet Grid */}
              <div className="grid grid-cols-5 md:grid-cols-6 lg:grid-cols-4 gap-2">
                {letters.map((letter) => (
                  <button
                    key={letter}
                    onClick={() => handleLetterClick(letter)}
                    className={`rounded-xl py-3 text-lg font-bold transition duration-200 transform hover:scale-105
                            ${selectedLetter === letter
                        ? "bg-pink-600 text-white shadow-xl ring-4 ring-pink-300 border-b-4 border-pink-800"
                        : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                      }
                            `}
                  >
                    {letter}
                  </button>
                ))}
              </div>

              {/* LETTER-FOR-WORD BUTTON */}
              <button
                onClick={handleKeywordSpeak}
                className={`w-full flex items-center justify-center p-3 rounded-xl font-bold transition duration-200 shadow-lg 
                    ${selectedLetter
                    ? 'bg-pink-500 text-white hover:bg-pink-600'
                    : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  }
                    `}
                disabled={!selectedLetter}
              >
                <Volume2 className="w-5 h-5 mr-2" />
                {selectedLetter} for {keyWord}
              </button>
            </div>
          </div>

          {/* ACTIVITY SIDEBAR */}
          <div>
            <h2 className="text-xl font-bold mb-3 text-purple-700 border-l-4 border-pink-400 pl-3">
              Interactive Activities:
            </h2>
            <div className="space-y-3">
              {activities.map((activity) => (
                <ActivityCard
                  key={activity.key}
                  title={activity.title}
                  icon={activity.icon}
                  color={activity.color}
                  activityKey={activity.key}
                  onClick={handleActivityClick}
                  isActive={selectedActivity?.key === activity.key}
                  status={activity.status} // Pass the status
                />
              ))}
            </div>
          </div>

        </div>

        {/* ======================= RIGHT COLUMN (MAIN CONTENT) ======================= */}
        <div className="lg:col-span-3 space-y-8">

          {/* -------------------- HERO LETTER CARD & PHONICS -------------------- */}
          <div className="bg-white p-6 rounded-3xl shadow-2xl border border-gray-100">
            <div className="flex justify-between items-center border-b pb-4 mb-4 border-purple-100">
              <div className="flex items-end gap-4">
                <h2 className="text-7xl font-extrabold text-purple-700 drop-shadow-md leading-none">
                  {selectedLetter}
                </h2>
                <h3 className="text-5xl font-light text-pink-500 leading-none">
                  {selectedLetter.toLowerCase()}
                </h3>
              </div>

              {/* Phonics Block - Replay letter sound */}
              <button
                onClick={() => speakText(selectedLetter, preferredVoice)}
                className="flex items-center bg-purple-100 px-4 py-2 rounded-full shadow-inner hover:bg-purple-200 transition"
                title="Click to hear the letter sound"
              >
                <Mic className="w-6 h-6 text-purple-600 mr-2" />
                <span className="text-xl font-semibold text-purple-700">
                  / {selectedLetter.toLowerCase().padEnd(2)} / Sound
                </span>
              </button>
            </div>

            {/* Word List within the Hero Card */}
            <h3 className="text-lg font-bold mb-3 text-gray-700">
              Key Words:
            </h3>
            <div className="flex flex-wrap gap-3">
              {words.map((w) => (
                <span
                  key={w}
                  className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full font-semibold text-base shadow-sm border border-yellow-300 transition hover:bg-yellow-200"
                >
                  {w}
                </span>
              ))}
            </div>
          </div>

          {/* -------------------- STORY SECTION -------------------- */}
          <div className="bg-white p-6 rounded-3xl shadow-xl border border-purple-100">
            <div className="flex items-center mb-4">
              <BookOpen className="w-6 h-6 text-pink-500 mr-3" />
              <h3 className="text-2xl font-bold text-purple-700">
                Story Time: The {selectedLetter} Tale
              </h3>
            </div>

            <div className="bg-purple-50 p-4 rounded-xl shadow-inner text-gray-700 leading-relaxed text-base border border-purple-100">
              <p
                dangerouslySetInnerHTML={{
                  __html: story.replace(/\*\*(.*?)\*\*/g, "<strong class='text-pink-600 font-extrabold'>$1</strong>")
                }}
              />
            </div>
            <button className="mt-4 w-full bg-purple-600 text-white px-4 py-2 rounded-xl font-semibold shadow-md hover:bg-purple-700 transition">
              Read More & Listen
            </button>
          </div>

          {/* -------------------- MAIN ACTIVITY SCREEN -------------------- */}
          <ActivityDisplay
            activity={selectedActivity}
            letter={selectedLetter}
            words={words}
            keyWord={keyWord} // Passed down the keyword
          />
        </div>
      </div>
    </div>
  );
};

export default AlphabetPage;